import {Box, Button, Link, Grid, useTheme} from '@mui/material';
import ResponsiveTypography from '../ui/responsive-typography';
import {useTranslation} from 'react-i18next';
import {formatDate} from '@/utils';
import {EventData} from '@/types/event';
import TypographyTitle from '../ui/typography-title';

interface OrderSummaryProps {
  onConfirm: () => void;
  onGoBack: () => void;
  event: EventData;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  onConfirm,
  onGoBack,
  event
}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, padding: 2}}>
      <TypographyTitle textKey={t('order_summary')} />

      <Box marginTop={4}>
        <ResponsiveTypography
          mediumSmall
          sx={{fontWeight: 'bold', display: 'inline'}}
        >
          {`${t('event_prop_title')}: `}
        </ResponsiveTypography>
        <ResponsiveTypography mediumSmall sx={{display: 'inline'}}>
          {event.title}
        </ResponsiveTypography>
      </Box>

      <Box>
        <ResponsiveTypography
          mediumSmall
          sx={{fontWeight: 'bold', display: 'inline'}}
        >
          {`${t('location')}: `}
        </ResponsiveTypography>
        <ResponsiveTypography mediumSmall sx={{display: 'inline'}}>
          {event.online ? t('online') : event.locationName}
        </ResponsiveTypography>
      </Box>

      <Box>
        <ResponsiveTypography
          mediumSmall
          sx={{fontWeight: 'bold', display: 'inline'}}
        >
          {`${t('date')}: `}
        </ResponsiveTypography>
        <ResponsiveTypography mediumSmall sx={{display: 'inline'}}>
          {formatDate(new Date(event.start), [])}
        </ResponsiveTypography>
      </Box>

      <Box>
        <ResponsiveTypography
          mediumSmall
          sx={{fontWeight: 'bold', display: 'inline'}}
        >
          {`${t('price')}: `}
        </ResponsiveTypography>
        <ResponsiveTypography mediumSmall sx={{display: 'inline'}}>
          {`${event.price.toFixed(2)}€`}
        </ResponsiveTypography>
      </Box>

      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <Link
          sx={{
            fontWeight: {
              xs: '1.1rem',
              sm: '1.4rem',
              md: '1.5rem'
            },
            textDecoration: 'none',
            color: theme.palette.customColors?.urlLink
          }}
          href={event.imprintLink || ''}
          target="_blank"
        >
          {`${t('imprint')} `}
        </Link>
        <ResponsiveTypography mediumSmall>
          {t('of_company', {
            companyName: event.createdBy?.name
          })}
        </ResponsiveTypography>
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <Link
          sx={{
            textDecoration: 'none',
            color: theme.palette.customColors?.urlLink,
            fontWeight: {
              xs: '1.1rem',
              sm: '1.4rem',
              md: '1.5rem'
            }
          }}
          href={event.termsOfUseLink || ''}
          target="_blank"
        >
          {t('terms_of_participation')}
        </Link>
        <ResponsiveTypography mediumSmall>
          {t('of_company', {
            companyName: event.createdBy?.name
          })}
        </ResponsiveTypography>
      </Box>

      {event.cancellationPolicyLink && (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Link
            sx={{
              fontWeight: {
                xs: '1.1rem',
                sm: '1.4rem',
                md: '1.5rem'
              },
              textDecoration: 'none',
              color: theme.palette.customColors?.urlLink
            }}
            href={event.cancellationPolicyLink}
            target="_blank"
          >
            {t('cancellation_policy')}
          </Link>
          <ResponsiveTypography mediumSmall>
            {t('of_company', {
              companyName: event.createdBy?.name
            })}
          </ResponsiveTypography>
        </Box>
      )}

      <Grid container spacing={2} sx={{marginTop: 2, justifyContent: 'center'}}>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{display: 'flex', justifyContent: 'center'}}
        >
          <Button
            onClick={onGoBack}
            variant="contained"
            color="primary"
            sx={{width: {xs: '100%', sm: '85%'}}}
          >
            {t('screen_back')}
          </Button>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{display: 'flex', justifyContent: 'center'}}
        >
          <Button
            onClick={onConfirm}
            variant="contained"
            color="primary"
            sx={{width: {xs: '100%', sm: '85%'}}}
          >
            {t('pay_and_order')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderSummary;
